import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Header } from '../common_v2/components/Header';
import { ButtonTheme, GenericPopup } from '@apply/components';
import {
    ManualAgreementStringBody,
    AgreementStringHeader,
    AutomaticAgreementStringBody,
} from '@apply/ui/src/lib/tables/draws/jsxConstants';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { DrawType } from '@apply/constants/src/draws/drawType';
import { IDrawBudget } from '@apply/api/src/api/DrawApi';
import { FeedbackModalV2 } from '@apply/ui/src/lib/tables/draws/modals/modalsv2/FeedbackModalV2';
import { SignaturePage } from '@apply/components/src/components/controls/SignaturePage';
import { useAuthentication } from '../Login/hooks/useAuthentication';

export const MobileDrawSignPage: React.FC = () => {
    const { authentication } = useAuthentication();
    const navigate = useNavigate();
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const drawApi = new DrawApi.DrawApi(applicationInsights);
    const pathnameParts = location.pathname.split('/');
    const drawBudgetId = parseInt(pathnameParts[2]);
    const drawId = parseInt(pathnameParts[3]);

    // Check if this is an approval scenario
    const params = new URLSearchParams(location.search);
    const isApprovalScenario = params.get('approve') === 'true';

    const [drawBudget, set_drawBudget] = useState<IDrawBudget>();
    const [ipAddress, set_ipAddress] = useState<string>();
    const { state } = useLocation() as { state: { automatic?: true | false } };
    const [isFeedbackOpen, set_isFeedbackOpen] = useState(false);
    const [isEndeavorToFundOpen, set_isEndeavorToFundOpen] = useState(false);

    const [disableActionButtons, set_disableActionButtons] = useState(false);

    const loadData = async () => {
        const response = await drawApi.getDrawBudget(drawBudgetId);
        if (response.statusCode !== 200) return;

        const drawBudget = response.result;
        set_drawBudget(drawBudget);
    };

    useEffect(() => {
        loadData();
    }, [drawBudgetId]);

    const onDeclineAgreement = () => {
        set_disableActionButtons(true);
        navigate(state?.automatic ? '/home' : `/mobilereviewandsubmit/${drawBudgetId}`);
    };

    const onSubmitAgreement = async () => {
        await brokerClientApi.getIpAddress().then((response) => {
            if (response.statusCode !== 200) return;
            set_ipAddress(response.result);
        });

        if (state?.automatic) {
            signAutomaticDraw();
        } else if (isApprovalScenario) {
            approveGCDraw();
        } else {
            signManualDraw();
        }
    };

    const signAutomaticDraw = async () => {
        set_disableActionButtons(true);

        const response = await drawApi.addDraw({
            drawBudgetId: drawBudgetId,
            drawType: DrawType.Automatic,
            fontId: 1,
            ipAddress: ipAddress,
        });

        if (response.statusCode !== 200) {
            set_disableActionButtons(false);
            return;
        }

        set_isEndeavorToFundOpen(true);
    };

    const approveGCDraw = async () => {
        set_disableActionButtons(true);

        const response = await drawApi.signGCRequestedDraw({
            drawId: drawId,
            fontId: 1,
            ipAddress: ipAddress,
        });

        if (response.statusCode !== 200) {
            set_disableActionButtons(false);
            return;
        }

        set_isFeedbackOpen(true);
    };

    const signManualDraw = async () => {
        if (!drawBudget) return;

        set_disableActionButtons(true);

        const latestDraw = drawBudget.draws[drawBudget.draws?.length - 1];

        const response = await drawApi.drawBorrowerSignature({
            drawId: latestDraw?.id,
            fontId: 1,
            ipAddress: ipAddress,
        });

        if (response.statusCode !== 200) {
            set_disableActionButtons(false);
            return;
        }

        set_isFeedbackOpen(true);
    };

    const onConfirmFeedback = async (category: number, type: number, comment: string) => {
        await drawApi.addFeedback({
            drawBudgetId: drawBudget?.id,
            feedbackCategory: category,
            feedbackType: type,
            comment: comment,
        });
        set_isFeedbackOpen(false);
    };

    const onCancelFeedback = () => {
        set_isFeedbackOpen(false);
        navigate('/home');
    };

    const handleEndeavorToFundClose = () => {
        set_isEndeavorToFundOpen(false);
        navigate('/home');
    };

    const endeavorToFundActionButtons = [
        {
            id: 'closeAutomaticSubmittedButton',
            theme: ButtonTheme.Primary,
            disabled: false,
            children: 'Close',
            width: '150px',
            handleClick: handleEndeavorToFundClose,
        },
    ];

    return (
        <>
            <div>
                <Header />
                <SignaturePage
                    agreementHeader={<AgreementStringHeader />}
                    agreementBody={state?.automatic ? <AutomaticAgreementStringBody /> : <ManualAgreementStringBody />}
                    onSubmitAgreement={onSubmitAgreement}
                    onDeclineAgreement={onDeclineAgreement}
                    disableButtons={disableActionButtons}
                    firstName={authentication?.user.firstName ?? ''}
                    lastName={authentication?.user.lastName ?? ''}
                />
            </div>
            <GenericPopup
                id={'DrawSubmittedPopup'}
                isOpen={isEndeavorToFundOpen}
                actionButtons={endeavorToFundActionButtons}
                handleClose={handleEndeavorToFundClose}
                aria-labelledby='alert-dialog-title'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title='Draw Submitted'
                width='400px'
                height='230px'
                maxWidth='90%'
                maxHeight='60%'
                buttonAlignment='center'
                wrapTitle={true}
            >
                <div>
                    <h5>Thank you!</h5>
                    <div style={{ paddingTop: '10px' }}>We will endeavor to fund within three business days.</div>
                </div>
            </GenericPopup>
            <FeedbackModalV2
                isOpen={isFeedbackOpen}
                confirmFeedbackCallback={onConfirmFeedback}
                cancelFeedbackCallback={onCancelFeedback}
            ></FeedbackModalV2>
        </>
    );
};
